import { format } from 'date-fns/esm';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Card,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Link
} from '@material-ui/core';

import CardContent from '../../../common/card/CardContent';
import StatusLabel from '../../../common/StatusLabel';
import { useGetAgreementStatusLogs } from '../Agreement.hooks';
import { Agreement, AgreementStatusLog } from '../Agreement.interface';
import { getPublishedStatusLabel } from '../../../common/PublishStatusLabels';

const StyledTypography = styled(Typography)`
  font-weight: 500;
`;

export interface AgreementStatusCardProps {
  agreement: Agreement;
}

const AgreementStatusCard: React.FC<AgreementStatusCardProps> = ({
  agreement
}) => {
  const {
    agreementStatusLogs: delta,
    isLoading,
    get
  } = useGetAgreementStatusLogs(agreement.id);
  const limit = 5;
  const [page, setPage] = useState(0);
  const agreementRef = useRef(agreement); // capturing previous agreement object
  const [agreementStatusLogs, setAgreementStatusLogs] = useState<
    AgreementStatusLog[]
  >([]);
  const statusLabel = getPublishedStatusLabel(agreement.status);
  useEffect(() => {
    if (agreement !== agreementRef.current) {
      // agreement object changed from previous; likely, status changed
      setAgreementStatusLogs([]);
      agreementRef.current = agreement;
      get({ page: 0, limit }); // fetch updated status change
    } else {
      get({ page, limit }); // get next page
    }
  }, [get, page, agreement]);
  useEffect(() => {
    if (delta && delta.data) {
      const logs = [
        ...agreementStatusLogs,
        ...delta.data.filter(
          newLog =>
            // don't insert log if it already exists; occurs when partial page load
            !agreementStatusLogs.find(log => log.id === newLog.id)
        )
      ];
      logs.sort(
        // resort the list after appending statuses
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
      setAgreementStatusLogs(logs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delta]);

  return (
    <Card>
      <CardContent background='primary'>
        <Typography
          variant='overline'
          style={{ margin: 0, paddingTop: '20px' }}
        >
          Status
        </Typography>
        <StyledTypography variant='h4'>{statusLabel}</StyledTypography>
      </CardContent>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>History</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>User</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {agreementStatusLogs &&
              agreementStatusLogs.map((statusLog, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {format(statusLog.created, 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>{statusLog.version}</TableCell>
                  <TableCell>
                    <StatusLabel status={statusLog.status} />
                  </TableCell>
                  <TableCell>
                    {statusLog.createdBy.firstName}{' '}
                    {statusLog.createdBy.lastName}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {delta && delta.total > (page + 1) * limit && (
        <Box textAlign='center' padding={2}>
          <Link
            component='button'
            variant='body2'
            onClick={() => setPage(page + 1)}
          >
            Load More
          </Link>
        </Box>
      )}
    </Card>
  );
};

export default AgreementStatusCard;
